import React, { useState, useEffect } from 'react'
import { alsokTheme } from '../styles/alsok-theme'
import { apiCall } from '../services/mockDataService'

const MeetingRoomManager = ({ onClose }) => {
  const [currentTab, setCurrentTab] = useState('calendar')
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    const start = new Date(today)
    // 月曜日を週の始まりとして計算（日曜日=0なので、月曜日にするには-1調整が必要）
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    start.setDate(today.getDate() - daysFromMonday)
    return start
  })
  const [showReservationModal, setShowReservationModal] = useState(false)
  const [editingReservation, setEditingReservation] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedReservationDetail, setSelectedReservationDetail] = useState(null)
  const [searchFilters, setSearchFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '10:00',
    capacity: 1
  })

  // 会議室データ - APIから取得
  const [meetingRooms, setMeetingRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // 予約データ - APIから取得
  const [reservations, setReservations] = useState([])
  const [statistics, setStatistics] = useState(null)
  const [selectedReservation, setSelectedReservation] = useState(null)

  // API呼び出し関数
  const fetchMeetingRooms = async () => {
    try {
      const data = await apiCall('/api/meetings/rooms')
      if (data.success) {
        setMeetingRooms(data.rooms.map(room => ({
          ...room,
          equipment: room.equipment ? room.equipment.split(',') : [],
          status: room.status || 'available'
        })))
      }
    } catch (error) {
      console.error('会議室データの取得に失敗しました:', error)
      setError('会議室データの取得に失敗しました')
    }
  }

  const fetchReservations = async () => {
    try {
      const data = await apiCall('/api/meetings/reservations')
      if (data.success) {
        setReservations(data.reservations.map(reservation => ({
          id: reservation.id,
          roomId: reservation.room_id,
          roomName: reservation.room_name,
          title: reservation.title || reservation.meeting_title,
          organizer: reservation.organizer || reservation.organizer_name,
          date: reservation.start_time.split('T')[0],
          startTime: reservation.start_time.split('T')[1].substring(0, 5),
          endTime: reservation.end_time.split('T')[1].substring(0, 5),
          attendees: parseInt(reservation.attendees) || 0,
          description: reservation.description || generateSampleDescription(reservation.title || reservation.meeting_title),
          status: reservation.status || 'confirmed'
        })))
      }
    } catch (error) {
      console.error('予約データの取得に失敗しました:', error)
      setError('予約データの取得に失敗しました')
    }
  }

  const fetchStatistics = async () => {
    try {
      const data = await apiCall('/api/meetings/statistics')
      if (data.success) {
        setStatistics(data.statistics)
      }
    } catch (error) {
      console.error('統計データの取得に失敗しました:', error)
    }
  }

  const fetchWeeklyReservations = async () => {
    try {
      const data = await apiCall('/api/meetings/reservations/weekly')
      if (data.success) {
        return data.reservations.map(reservation => ({
          id: reservation.id,
          roomId: reservation.room_id,
          roomName: reservation.room_name,
          title: reservation.meeting_title,
          organizer: reservation.organizer_name,
          date: reservation.start_time.split('T')[0],
          startTime: reservation.start_time.split('T')[1].substring(0, 5),
          endTime: reservation.end_time.split('T')[1].substring(0, 5),
          attendees: parseInt(reservation.attendees) || 0,
          description: reservation.description || '',
          status: 'confirmed'
        }))
      }
      return []
    } catch (error) {
      console.error('週間予約データの取得に失敗しました:', error)
      return []
    }
  }

  // 初期化時のデータ読み込み
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        await Promise.all([
          fetchMeetingRooms(),
          fetchReservations(),
          fetchStatistics()
        ])
      } catch (error) {
        console.error('データの読み込みに失敗しました:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const [newReservation, setNewReservation] = useState({
    roomId: '',
    title: '',
    organizer: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '10:00',
    attendees: 1,
    description: ''
  })

  // 今日の予約数を取得
  const todayReservations = reservations.filter(r => r.date === new Date().toISOString().split('T')[0])
  
  // 今週の予約数を取得
  const [weeklyReservations, setWeeklyReservations] = useState([])
  
  const getThisWeekReservations = () => {
    return weeklyReservations
  }

  // 今週の予約データを取得
  useEffect(() => {
    const loadWeeklyData = async () => {
      const weekly = await fetchWeeklyReservations()
      setWeeklyReservations(weekly)
    }
    loadWeeklyData()
  }, [reservations, currentWeekStart])

  // 空室検索
  const [availableRooms, setAvailableRooms] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  
  const searchAvailableRooms = async () => {
    const { date, startTime, endTime, capacity } = searchFilters
    
    setSearchLoading(true)
    try {
      const params = new URLSearchParams({
        date,
        startTime,
        endTime,
        capacity: capacity.toString()
      })
      
      const data = await apiCall(`/api/meetings/rooms/available?${params}`)
      
      if (data.success) {
        setAvailableRooms(data.rooms)
      } else {
        setAvailableRooms([])
      }
    } catch (error) {
      console.error('空室検索に失敗しました:', error)
      setAvailableRooms([])
    } finally {
      setSearchLoading(false)
    }
  }

  const getAvailableRooms = () => {
    return availableRooms
  }

  // 検索フィルタが変更されたときに自動検索
  useEffect(() => {
    if (searchFilters.date && searchFilters.startTime && searchFilters.endTime) {
      searchAvailableRooms()
    }
  }, [searchFilters])

  // 予約作成
  const handleCreateReservation = async () => {
    const room = meetingRooms.find(r => r.id === parseInt(newReservation.roomId))
    if (!room) return

    try {
      const startDateTime = `${newReservation.date}T${newReservation.startTime}:00`
      const endDateTime = `${newReservation.date}T${newReservation.endTime}:00`
      
      const data = await apiCall('/api/meetings/reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meetingTitle: newReservation.title,
          organizerEmail: `${newReservation.organizer}@example.com`,
          organizerName: newReservation.organizer,
          roomId: parseInt(newReservation.roomId),
          startTime: startDateTime,
          endTime: endDateTime,
          attendees: newReservation.attendees.toString(),
          description: newReservation.description
        })
      })

      if (data.success) {
        // 予約リストを再取得
        await fetchReservations()
        alert('予約が作成されました')
        
        setNewReservation({
          roomId: '',
          title: '',
          organizer: '',
          date: new Date().toISOString().split('T')[0],
          startTime: '09:00',
          endTime: '10:00',
          attendees: 1,
          description: ''
        })
        setShowReservationModal(false)
      } else {
        alert('予約の作成に失敗しました: ' + data.error)
      }
    } catch (error) {
      console.error('予約作成エラー:', error)
      alert('予約の作成に失敗しました')
    }
  }

  // 予約編集
  const handleEditReservation = (reservation) => {
    setEditingReservation(reservation)
    setNewReservation({
      roomId: reservation.roomId,
      title: reservation.title,
      organizer: reservation.organizer,
      date: reservation.date,
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      attendees: reservation.attendees,
      description: reservation.description
    })
    setShowReservationModal(true)
  }

  // 予約更新
  const handleUpdateReservation = async () => {
    const room = meetingRooms.find(r => r.id === parseInt(newReservation.roomId))
    if (!room) return

    try {
      const startDateTime = `${newReservation.date}T${newReservation.startTime}:00`
      const endDateTime = `${newReservation.date}T${newReservation.endTime}:00`
      
      const data = await apiCall(`/api/meetings/reservations/${editingReservation.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meetingTitle: newReservation.title,
          organizerEmail: `${newReservation.organizer}@example.com`,
          organizerName: newReservation.organizer,
          roomId: parseInt(newReservation.roomId),
          startTime: startDateTime,
          endTime: endDateTime,
          attendees: newReservation.attendees.toString(),
          description: newReservation.description
        })
      })

      if (data.success) {
        // 予約リストを再取得
        await fetchReservations()
        alert('予約が更新されました')
        
        setEditingReservation(null)
        setShowReservationModal(false)
        setNewReservation({
          roomId: '',
          title: '',
          organizer: '',
          date: new Date().toISOString().split('T')[0],
          startTime: '09:00',
          endTime: '10:00',
          attendees: 1,
          description: ''
        })
      } else {
        alert('予約の更新に失敗しました: ' + data.error)
      }
    } catch (error) {
      console.error('予約更新エラー:', error)
      alert('予約の更新に失敗しました')
    }
  }

  // 予約削除
  const handleDeleteReservation = async (reservationId) => {
    if (confirm('この予約を削除しますか？')) {
      try {
        const data = await apiCall(`/api/meetings/reservations/${reservationId}`, {
          method: 'DELETE'
        })

        if (data.success) {
          // 予約リストを再取得
          await fetchReservations()
          alert('予約が削除されました')
        } else {
          alert('予約の削除に失敗しました: ' + data.error)
        }
      } catch (error) {
        console.error('予約削除エラー:', error)
        alert('予約の削除に失敗しました')
      }
    }
  }

  // 週間ナビゲーション関数
  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart)
    newWeekStart.setDate(currentWeekStart.getDate() + (direction === 'next' ? 7 : -7))
    setCurrentWeekStart(newWeekStart)
  }

  // 現在の週の日付を取得
  const getCurrentWeekDates = () => {
    const dates = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart)
      date.setDate(currentWeekStart.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  // タイムスロット生成（改善版）
  const generateTimeSlots = () => {
    const slots = []
    for (let hour = 8; hour <= 20; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`)
      if (hour < 20) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`)
      }
    }
    return slots
  }

  // 予約の色分け関数
  const getReservationColor = (reservation) => {
    if (reservation.facePassEnabled) {
      return 'linear-gradient(135deg, #10b981 0%, #059669 100%)' // 緑: 顔パス有効
    }
    if (reservation.attendees > 5) {
      return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' // オレンジ: 大規模会議
    }
    return 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' // 青: 標準会議
  }

  // 予約時間の計算
  const calculateReservationHeight = (startTime, endTime) => {
    const start = new Date(`2000-01-01T${startTime}:00`)
    const end = new Date(`2000-01-01T${endTime}:00`)
    const duration = (end - start) / (1000 * 60) // 分単位
    return Math.max(Math.floor(duration / 30) * 50, 50) // 最低50px
  }

  // 予約時間の重複チェック
  const getReservationPosition = (reservation, allReservations) => {
    const overlapping = allReservations.filter(r => 
      r.roomId === reservation.roomId && 
      r.id !== reservation.id &&
      ((r.startTime < reservation.endTime && r.endTime > reservation.startTime))
    )
    return overlapping.length > 0 ? overlapping.length * 5 : 0
  }

  // サンプル説明文生成
  const generateSampleDescription = (title) => {
    const descriptions = {
      '企画会議': '新プロジェクトの企画立案について話し合います。\n・企画書の準備をお願いします\n・資料配布済み\n・プロジェクター使用予定',
      'プロジェクト進捗会議': '各チームの進捗状況を確認し、課題を共有します。\n・進捗レポートの持参をお願いします\n・次週のスケジュール調整\n・リソース配分の検討',
      '月次報告会': '月次業績の発表と来月の計画について議論します。\n・月次レポートの準備\n・KPI達成状況の確認\n・来月の目標設定',
      'クライアント商談': '新規クライアントとの商談を行います。\n・提案書の最終確認\n・デモンストレーション準備\n・契約条件の調整',
      '戦略会議': '今後の事業戦略について検討します。\n・市場分析資料の準備\n・競合他社の動向確認\n・新戦略の立案',
      'チームミーティング': '定例のチームミーティングです。\n・週次タスクの確認\n・問題点の共有と解決策の検討\n・来週のスケジュール調整',
      'システムレビュー': 'システムの改善点について検討します。\n・現状の問題点整理\n・改善提案の検討\n・実装スケジュールの確認',
      'AIプロジェクト会議': 'AI導入プロジェクトの進捗確認です。\n・技術仕様の確認\n・データ準備状況の報告\n・テスト計画の検討'
    }
    return descriptions[title] || '会議の詳細については、主催者にお問い合わせください。'
  }

  // 予約詳細表示
  const showReservationDetail = (reservation) => {
    setSelectedReservationDetail(reservation)
    setShowDetailModal(true)
  }

  // 利用統計データ
  const getRoomStats = () => {
    if (!statistics || !statistics.roomStats) {
      return meetingRooms.map(room => ({
        ...room,
        reservation_count: 0,
        total_hours: 0,
        utilization_rate: 0
      }))
    }
    
    return statistics.roomStats.map(roomStat => {
      const room = meetingRooms.find(r => r.id === roomStat.id) || {}
      return {
        ...room,
        ...roomStat,
        reservationCount: roomStat.reservation_count,
        totalHours: roomStat.total_hours || 0,
        utilizationRate: roomStat.utilization_rate || 0
      }
    })
  }

  // ローディング中の表示
  if (loading) {
    return (
      <div className="meeting-room-manager">
        <div className="manager-header">
          <div className="header-left">
            <h1>🏢 会議室管理システム</h1>
          </div>
          <div className="header-right">
            <button onClick={onClose} className="close-button">
              ← 管理画面に戻る
            </button>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner">読み込み中...</div>
        </div>
      </div>
    )
  }

  // エラー時の表示
  if (error) {
    return (
      <div className="meeting-room-manager">
        <div className="manager-header">
          <div className="header-left">
            <h1>🏢 会議室管理システム</h1>
          </div>
          <div className="header-right">
            <button onClick={onClose} className="close-button">
              ← 管理画面に戻る
            </button>
          </div>
        </div>
        <div className="error-container">
          <div className="error-message">
            <h2>エラーが発生しました</h2>
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="retry-button">
              再読み込み
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="meeting-room-manager">
      {/* ヘッダー */}
      <div className="manager-header">
        <div className="header-left">
          <h1>🏢 会議室管理システム</h1>
        </div>
        <div className="header-right">
          <button onClick={onClose} className="close-button">
            ← 管理画面に戻る
          </button>
        </div>
      </div>

      {/* サマリーカード */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon">📅</div>
          <div className="card-content">
            <div className="card-number">{todayReservations.length}</div>
            <div className="card-label">本日の予約</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <div className="card-number">{getThisWeekReservations().length}</div>
            <div className="card-label">今週の予約</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">🏠</div>
          <div className="card-content">
            <div className="card-number">{meetingRooms.filter(r => r.status === 'available').length}</div>
            <div className="card-label">利用可能会議室</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">🔧</div>
          <div className="card-content">
            <div className="card-number">{meetingRooms.filter(r => r.status === 'maintenance').length}</div>
            <div className="card-label">メンテナンス中</div>
          </div>
        </div>
      </div>

      {/* タブナビゲーション */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${currentTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setCurrentTab('calendar')}
        >
          📅 予約カレンダー
        </button>
        <button 
          className={`tab-button ${currentTab === 'reservations' ? 'active' : ''}`}
          onClick={() => setCurrentTab('reservations')}
        >
          📝 予約管理
        </button>
        <button 
          className={`tab-button ${currentTab === 'search' ? 'active' : ''}`}
          onClick={() => setCurrentTab('search')}
        >
          🔍 空室検索
        </button>
        <button 
          className={`tab-button ${currentTab === 'stats' ? 'active' : ''}`}
          onClick={() => setCurrentTab('stats')}
        >
          📊 利用統計
        </button>
      </div>

      {/* タブコンテンツ */}
      <div className="tab-content">
        {/* 予約カレンダー */}
        {currentTab === 'calendar' && (
          <div className="calendar-view">
            <div className="calendar-header">
              <div className="weekly-header">
                <div className="week-navigation">
                  <button 
                    className="nav-button prev-week"
                    onClick={() => navigateWeek('prev')}
                  >
                    ‹
                  </button>
                  <div className="week-dates">
                    {getCurrentWeekDates().map((date, i) => {
                      const dayNames = ['月', '火', '水', '木', '金', '土', '日']
                      const isToday = date.toDateString() === new Date().toDateString()
                      return (
                        <div key={i} className={`week-day ${isToday ? 'today' : ''}`}>
                          <div className="day-name">{dayNames[i]}</div>
                          <div className="day-number">{date.getDate()}</div>
                        </div>
                      )
                    })}
                  </div>
                  <button 
                    className="nav-button next-week"
                    onClick={() => navigateWeek('next')}
                  >
                    ›
                  </button>
                </div>
              </div>
              <button 
                className="create-button"
                onClick={() => setShowReservationModal(true)}
              >
                ➕ 新規予約
              </button>
            </div>
            
            <div className="weekly-calendar-grid">
              <div className="time-column">
                <div className="time-header">GMT+09</div>
                {generateTimeSlots().map(time => (
                  <div key={time} className="time-slot">
                    {time.split(':')[0] === '09' ? '午前9時' : 
                     time.split(':')[0] === '10' ? '午前10時' : 
                     time.split(':')[0] === '11' ? '午前11時' : 
                     time.split(':')[0] === '12' ? '午後12時' : 
                     time.split(':')[0] === '13' ? '午後1時' : 
                     time.split(':')[0] === '14' ? '午後2時' : 
                     time.split(':')[0] === '15' ? '午後3時' : 
                     time.split(':')[0] === '16' ? '午後4時' : 
                     time.split(':')[0] === '17' ? '午後5時' : 
                     time.split(':')[0] === '18' ? '午後6時' : 
                     time.split(':')[0] === '19' ? '午後7時' : 
                     time.split(':')[0] === '20' ? '午後8時' : time}
                  </div>
                ))}
              </div>
              
              {getCurrentWeekDates().map((date, dayIndex) => {
                const dateStr = date.toISOString().split('T')[0]
                const dayReservations = reservations.filter(r => r.date === dateStr)
                
                return (
                  <div key={dayIndex} className="day-column">
                    <div className="day-header">
                      <div className="day-info">
                        <div className="day-name">{['月', '火', '水', '木', '金', '土', '日'][dayIndex]}</div>
                        <div className="day-number">{date.getDate()}</div>
                      </div>
                    </div>
                    
                    <div className="day-schedule">
                      {generateTimeSlots().map((time, timeIndex) => {
                        const reservation = dayReservations.find(r => 
                          r.startTime <= time && 
                          r.endTime > time
                        )
                        
                        // 現在時刻の判定
                        const currentHour = new Date().getHours()
                        const currentMinute = new Date().getMinutes()
                        const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${currentMinute < 30 ? '00' : '30'}`
                        const isCurrentTime = time === currentTimeStr && date.toDateString() === new Date().toDateString()
                        
                        return (
                          <div 
                            key={time} 
                            className={`schedule-slot ${reservation ? 'occupied' : 'available'} ${isCurrentTime ? 'current-time' : ''}`}
                            onClick={() => !reservation && setShowReservationModal(true)}
                            style={{ 
                              cursor: reservation ? 'pointer' : 'default',
                              position: 'relative'
                            }}
                          >
                            {reservation && time === reservation.startTime && (
                              <div 
                                className="reservation-card"
                                style={{
                                  background: '#e3f2fd',
                                  border: '1px solid #2196f3',
                                  borderRadius: '8px',
                                  padding: '8px',
                                  margin: '2px',
                                  height: `${calculateReservationHeight(reservation.startTime, reservation.endTime) - 4}px`,
                                  minHeight: '40px',
                                  overflow: 'hidden',
                                  position: 'absolute',
                                  left: '2px',
                                  right: '2px',
                                  top: '2px',
                                  zIndex: 10
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  showReservationDetail(reservation)
                                }}
                                onMouseEnter={(e) => {
                                  const tooltip = document.createElement('div')
                                  tooltip.className = 'reservation-tooltip'
                                  tooltip.innerHTML = `
                                    <div><strong>${reservation.title}</strong></div>
                                    <div>👤 ${reservation.organizer}</div>
                                    <div>⏰ ${reservation.startTime} - ${reservation.endTime}</div>
                                    <div>👥 ${reservation.attendees}名</div>
                                    ${reservation.facePassEnabled ? '<div>🔐 顔パス有効</div>' : ''}
                                  `
                                  tooltip.style.cssText = `
                                    position: absolute;
                                    background: rgba(0, 0, 0, 0.9);
                                    color: white;
                                    padding: 8px 12px;
                                    border-radius: 6px;
                                    font-size: 12px;
                                    z-index: 1000;
                                    pointer-events: none;
                                    white-space: nowrap;
                                    top: ${e.clientY + 10}px;
                                    left: ${e.clientX + 10}px;
                                  `
                                  document.body.appendChild(tooltip)
                                  
                                  e.target.addEventListener('mouseleave', () => {
                                    document.body.removeChild(tooltip)
                                  }, { once: true })
                                }}
                              >
                                <div className="reservation-title" style={{
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: '#1976d2',
                                  marginBottom: '2px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {reservation.title}
                                </div>
                                <div className="reservation-time" style={{
                                  fontSize: '10px',
                                  color: '#666',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  午前{reservation.startTime.replace(':', ':')}
                                </div>
                              </div>
                            )}
                            
                            {isCurrentTime && (
                              <div className="current-time-line" style={{
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                right: '0',
                                height: '2px',
                                background: '#ef4444',
                                zIndex: 20
                              }}>
                                <div className="current-time-dot" style={{
                                  position: 'absolute',
                                  left: '-4px',
                                  top: '-3px',
                                  width: '8px',
                                  height: '8px',
                                  background: '#ef4444',
                                  borderRadius: '50%',
                                  animation: 'pulse 2s infinite'
                                }}></div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* 予約管理 */}
        {currentTab === 'reservations' && (
          <div className="reservations-view">
            <div className="reservations-header">
              <h2>📝 予約一覧</h2>
              <button 
                className="create-button"
                onClick={() => setShowReservationModal(true)}
              >
                ➕ 新規予約
              </button>
            </div>
            
            <div className="reservations-table">
              <div className="table-header">
                <div className="col-date">日付</div>
                <div className="col-time">時間</div>
                <div className="col-room">会議室</div>
                <div className="col-title">件名</div>
                <div className="col-organizer">主催者</div>
                <div className="col-attendees">参加者数</div>
                <div className="col-actions">操作</div>
              </div>
              
              {reservations
                .sort((a, b) => new Date(a.date + ' ' + a.startTime) - new Date(b.date + ' ' + b.startTime))
                .map(reservation => (
                <div key={reservation.id} className="table-row">
                  <div className="col-date">{new Date(reservation.date).toLocaleDateString('ja-JP')}</div>
                  <div className="col-time">{reservation.startTime} - {reservation.endTime}</div>
                  <div className="col-room">{reservation.roomName}</div>
                  <div className="col-title">{reservation.title}</div>
                  <div className="col-organizer">{reservation.organizer}</div>
                  <div className="col-attendees">{reservation.attendees}名</div>
                  <div className="col-actions">
                    <button 
                      className="edit-button"
                      onClick={() => handleEditReservation(reservation)}
                    >
                      ✏️
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteReservation(reservation.id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* 空室検索 */}
        {currentTab === 'search' && (
          <div className="search-view">
            <h2>🔍 空室検索</h2>
            
            <div className="search-filters">
              <div className="filter-group">
                <label>日付</label>
                <input 
                  type="date"
                  value={searchFilters.date}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              
              <div className="filter-group">
                <label>開始時間</label>
                <select 
                  value={searchFilters.startTime}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, startTime: e.target.value }))}
                >
                  {generateTimeSlots().map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>終了時間</label>
                <select 
                  value={searchFilters.endTime}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, endTime: e.target.value }))}
                >
                  {generateTimeSlots().map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>必要人数</label>
                <input 
                  type="number"
                  min="1"
                  max="50"
                  value={searchFilters.capacity}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                />
              </div>
            </div>
            
            <div className="search-results">
              <h3>検索結果 ({getAvailableRooms().length}件)</h3>
              
              {searchLoading ? (
                <div className="search-loading">
                  <div className="loading-spinner">検索中...</div>
                </div>
              ) : getAvailableRooms().length === 0 ? (
                <div className="no-results">
                  指定された条件で利用可能な会議室はありません。
                </div>
              ) : (
                <div className="available-rooms">
                  {getAvailableRooms().map(room => (
                    <div key={room.id} className="available-room-card">
                      <div className="room-info">
                        <h4>{room.name}</h4>
                        <p>📍 {room.location} | 👥 {room.capacity}名</p>
                        <div className="equipment-tags">
                          {room.equipment.map(eq => (
                            <span key={eq} className="equipment-tag small">{eq}</span>
                          ))}
                        </div>
                      </div>
                      <button 
                        className="quick-reserve-button"
                        onClick={() => {
                          setNewReservation(prev => ({
                            ...prev,
                            roomId: room.id,
                            date: searchFilters.date,
                            startTime: searchFilters.startTime,
                            endTime: searchFilters.endTime,
                            attendees: searchFilters.capacity
                          }))
                          setShowReservationModal(true)
                        }}
                      >
                        即予約
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 利用統計 */}
        {currentTab === 'stats' && (
          <div className="stats-view">
            <h2>📊 会議室利用統計</h2>
            
            <div className="stats-cards">
              {getRoomStats().map(room => (
                <div key={room.id} className="stats-card">
                  <div className="stats-header">
                    <h3>{room.name}</h3>
                    <span className={`utilization-rate ${room.utilizationRate > 70 ? 'high' : room.utilizationRate > 40 ? 'medium' : 'low'}`}>
                      {room.utilizationRate}%
                    </span>
                  </div>
                  
                  <div className="stats-details">
                    <div className="stat-item">
                      <span className="stat-label">📅 予約回数</span>
                      <span className="stat-value">{room.reservationCount}回</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">⏰ 利用時間</span>
                      <span className="stat-value">{room.totalHours.toFixed(1)}時間</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">👥 収容人数</span>
                      <span className="stat-value">{room.capacity}名</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">📍 場所</span>
                      <span className="stat-value">{room.location}</span>
                    </div>
                  </div>
                  
                  <div className="utilization-bar">
                    <div 
                      className="utilization-fill"
                      style={{ width: `${room.utilizationRate}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 予約作成/編集モーダル */}
      {showReservationModal && (
        <div className="modal-overlay">
          <div className="reservation-modal">
            <div className="modal-header">
              <h2>{editingReservation ? '予約編集' : '新規予約'}</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowReservationModal(false)
                  setEditingReservation(null)
                  setNewReservation({
                    roomId: '',
                    title: '',
                    organizer: '',
                    date: new Date().toISOString().split('T')[0],
                    startTime: '09:00',
                    endTime: '10:00',
                    attendees: 1,
                    description: ''
                  })
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>会議室</label>
                <select 
                  value={newReservation.roomId}
                  onChange={(e) => setNewReservation(prev => ({ ...prev, roomId: e.target.value }))}
                >
                  <option value="">選択してください</option>
                  {meetingRooms
                    .filter(room => room.status === 'available')
                    .map(room => (
                    <option key={room.id} value={room.id}>
                      {room.name} (👥 {room.capacity}名)
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>件名</label>
                <input 
                  type="text"
                  value={newReservation.title}
                  onChange={(e) => setNewReservation(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="会議の件名を入力"
                />
              </div>
              
              <div className="form-group">
                <label>主催者</label>
                <input 
                  type="text"
                  value={newReservation.organizer}
                  onChange={(e) => setNewReservation(prev => ({ ...prev, organizer: e.target.value }))}
                  placeholder="主催者名を入力"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>日付</label>
                  <input 
                    type="date"
                    value={newReservation.date}
                    onChange={(e) => setNewReservation(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>参加者数</label>
                  <input 
                    type="number"
                    min="1"
                    max="50"
                    value={newReservation.attendees}
                    onChange={(e) => setNewReservation(prev => ({ ...prev, attendees: parseInt(e.target.value) }))}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>開始時間</label>
                  <select 
                    value={newReservation.startTime}
                    onChange={(e) => setNewReservation(prev => ({ ...prev, startTime: e.target.value }))}
                  >
                    {generateTimeSlots().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>終了時間</label>
                  <select 
                    value={newReservation.endTime}
                    onChange={(e) => setNewReservation(prev => ({ ...prev, endTime: e.target.value }))}
                  >
                    {generateTimeSlots().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>説明</label>
                <textarea 
                  value={newReservation.description}
                  onChange={(e) => setNewReservation(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="会議の詳細や準備事項など"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => {
                  setShowReservationModal(false)
                  setEditingReservation(null)
                }}
              >
                キャンセル
              </button>
              <button 
                className="submit-button"
                onClick={editingReservation ? handleUpdateReservation : handleCreateReservation}
                disabled={!newReservation.roomId || !newReservation.title || !newReservation.organizer}
              >
                {editingReservation ? '更新' : '予約'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 予約詳細モーダル */}
      {showDetailModal && selectedReservationDetail && (
        <div className="modal-overlay">
          <div className="reservation-detail-modal">
            <div className="detail-modal-header">
              <div className="header-actions">
                <button className="action-btn edit-btn" onClick={() => {
                  setEditingReservation(selectedReservationDetail)
                  setNewReservation({
                    roomId: selectedReservationDetail.roomId,
                    title: selectedReservationDetail.title,
                    organizer: selectedReservationDetail.organizer,
                    date: selectedReservationDetail.date,
                    startTime: selectedReservationDetail.startTime,
                    endTime: selectedReservationDetail.endTime,
                    attendees: selectedReservationDetail.attendees,
                    description: selectedReservationDetail.description || ''
                  })
                  setShowDetailModal(false)
                  setShowReservationModal(true)
                }}>
                  ✏️
                </button>
                <button className="action-btn delete-btn" onClick={() => {
                  if (confirm('この予約を削除しますか？')) {
                    handleDeleteReservation(selectedReservationDetail.id)
                    setShowDetailModal(false)
                  }
                }}>
                  🗑️
                </button>
                <button className="action-btn close-btn" onClick={() => setShowDetailModal(false)}>×</button>
              </div>
            </div>
            
            <div className="detail-modal-body">
              <div className="meeting-info">
                <div className="meeting-status">
                  <span className="status-dot"></span>
                  <span className="meeting-title">{selectedReservationDetail.title}</span>
                </div>
              </div>
              
              <div className="meeting-details">
                <div className="detail-item">
                  <span className="detail-icon">🏢</span>
                  <div className="detail-content">
                    <div className="detail-title">会議室</div>
                    <div className="detail-value">{selectedReservationDetail.roomName}</div>
                  </div>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">📅</span>
                  <div className="detail-content">
                    <div className="detail-title">日付</div>
                    <div className="detail-value">
                      {new Date(selectedReservationDetail.date).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">⏰</span>
                  <div className="detail-content">
                    <div className="detail-title">時間</div>
                    <div className="detail-value">
                      {selectedReservationDetail.startTime} ～ {selectedReservationDetail.endTime}
                    </div>
                  </div>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">👤</span>
                  <div className="detail-content">
                    <div className="detail-title">主催者</div>
                    <div className="detail-value">{selectedReservationDetail.organizer}</div>
                  </div>
                </div>
                
                <div className="detail-item">
                  <span className="detail-icon">👥</span>
                  <div className="detail-content">
                    <div className="detail-title">参加人数</div>
                    <div className="detail-value">{selectedReservationDetail.attendees}名</div>
                  </div>
                </div>
                
                {selectedReservationDetail.description && (
                  <div className="detail-item">
                    <span className="detail-icon">📝</span>
                    <div className="detail-content">
                      <div className="detail-title">説明</div>
                      <div className="detail-value detail-description">
                        {selectedReservationDetail.description}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .meeting-room-manager {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          font-family: ${alsokTheme.typography.fontFamily.primary};
        }

        .manager-header {
          background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%);
          color: white;
          padding: 20px 32px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.2);
        }

        .manager-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
        }

        .close-button {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.3);
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 600;
        }

        .close-button:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          padding: 24px 32px;
        }

        .summary-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .summary-card .card-icon {
          font-size: 32px;
          width: 48px;
          text-align: center;
        }

        .summary-card .card-number {
          font-size: 24px;
          font-weight: 700;
          color: #1e40af;
          line-height: 1;
        }

        .summary-card .card-label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }

        .tab-navigation {
          display: flex;
          background: white;
          margin: 0 32px;
          border-radius: 12px 12px 0 0;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .tab-button {
          flex: 1;
          padding: 16px 20px;
          border: none;
          background: white;
          cursor: pointer;
          font-weight: 600;
          color: #6b7280;
          transition: all 0.3s ease;
          border-bottom: 3px solid transparent;
        }

        .tab-button:hover {
          background: #f8fafc;
        }

        .tab-button.active {
          color: #1e40af;
          border-bottom-color: #1e40af;
          background: #f8fafc;
        }

        .tab-content {
          background: white;
          margin: 0 32px 32px 32px;
          border-radius: 0 0 12px 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          min-height: 600px;
        }

        /* カレンダービュー */
        .calendar-view {
          padding: 24px;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .weekly-header {
          flex: 1;
        }

        .week-navigation {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .nav-button {
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          transition: all 0.2s ease;
        }

        .nav-button:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .week-dates {
          display: flex;
          gap: 8px;
        }

        .week-day {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px 12px;
          border-radius: 8px;
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          min-width: 50px;
        }

        .week-day.today {
          background: #3b82f6;
          color: white;
          border-color: #2563eb;
        }

        .week-day .day-name {
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .week-day .day-number {
          font-size: 18px;
          font-weight: 700;
        }

        .week-day.today .day-number {
          background: white;
          color: #3b82f6;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .create-button {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .create-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .weekly-calendar-grid {
          display: flex;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }

        .time-column {
          width: 100px;
          background: #f8fafc;
          border-right: 1px solid #e5e7eb;
        }

        .time-header {
          padding: 12px;
          font-weight: 600;
          text-align: center;
          border-bottom: 1px solid #e5e7eb;
          background: #e2e8f0;
          font-size: 12px;
          color: #6b7280;
        }

        .time-slot {
          padding: 8px 4px;
          text-align: center;
          font-size: 11px;
          border-bottom: 1px solid #e5e7eb;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 500;
          color: #6b7280;
        }
        
        .time-slot:nth-child(even) {
          background: #f1f5f9;
        }

        .day-column {
          flex: 1;
          border-right: 1px solid #e5e7eb;
        }

        .day-column:last-child {
          border-right: none;
        }

        .day-header {
          padding: 12px;
          text-align: center;
          border-bottom: 1px solid #e5e7eb;
          background: #e2e8f0;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .day-info {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .day-info .day-name {
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #6b7280;
        }

        .day-info .day-number {
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
        }

        .day-schedule {
          position: relative;
        }

        .schedule-slot {
          height: 50px;
          border-bottom: 1px solid #e5e7eb;
          position: relative;
          transition: all 0.2s ease;
        }

        .schedule-slot.available {
          background: #ffffff;
          cursor: pointer;
        }

        .schedule-slot.available:hover {
          background: #f8fafc;
        }

        .schedule-slot.occupied {
          background: #ffffff;
        }

        .schedule-slot.current-time {
          background: #ffffff;
        }

        .reservation-card {
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .reservation-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .current-time-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #ef4444;
          z-index: 20;
        }

        .current-time-dot {
          position: absolute;
          left: -4px;
          top: -3px;
          width: 8px;
          height: 8px;
          background: #ef4444;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }

        .face-pass-badge {
          margin-left: 4px;
          background: rgba(255, 255, 255, 0.2);
          padding: 2px 4px;
          border-radius: 3px;
          font-size: 10px;
        }

        .reservation-tooltip {
          position: absolute;
          background: rgba(0, 0, 0, 0.9);
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          z-index: 1000;
          pointer-events: none;
          white-space: nowrap;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* 予約管理ビュー */
        .reservations-view {
          padding: 24px;
        }

        .reservations-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .reservations-header h2 {
          margin: 0;
          color: #1e40af;
          font-size: 20px;
        }

        .reservations-table {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }

        .table-header {
          display: grid;
          grid-template-columns: 100px 120px 100px 1fr 120px 80px 80px;
          background: #f8fafc;
          border-bottom: 1px solid #e5e7eb;
          font-weight: 600;
          padding: 12px 0;
        }

        .table-header > div {
          padding: 0 12px;
          display: flex;
          align-items: center;
        }

        .table-row {
          display: grid;
          grid-template-columns: 100px 120px 100px 1fr 120px 80px 80px;
          border-bottom: 1px solid #e5e7eb;
          padding: 12px 0;
          transition: background 0.2s ease;
        }

        .table-row:hover {
          background: #f8fafc;
        }

        .table-row > div {
          padding: 0 12px;
          display: flex;
          align-items: center;
        }

        .edit-button, .delete-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background 0.2s ease;
        }

        .edit-button:hover {
          background: #dbeafe;
        }

        .delete-button:hover {
          background: #fecaca;
        }


        /* 空室検索ビュー */
        .search-view {
          padding: 24px;
        }

        .search-view h2 {
          margin: 0 0 24px 0;
          color: #1e40af;
          font-size: 20px;
        }

        .search-filters {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-group label {
          font-weight: 600;
          font-size: 14px;
          color: #374151;
        }

        .filter-group input,
        .filter-group select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .search-results h3 {
          margin: 0 0 16px 0;
          color: #1e40af;
          font-size: 18px;
        }

        .no-results {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          font-style: italic;
        }

        .search-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px;
        }

        .available-rooms {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .available-room-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .available-room-card:hover {
          background: #f8fafc;
        }

        .room-info h4 {
          margin: 0 0 8px 0;
          color: #1e40af;
          font-size: 16px;
        }

        .room-info p {
          margin: 0 0 8px 0;
          color: #6b7280;
          font-size: 14px;
        }

        .equipment-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .quick-reserve-button {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .quick-reserve-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
        }

        /* 利用統計ビュー */
        .stats-view {
          padding: 24px;
        }

        .stats-view h2 {
          margin: 0 0 24px 0;
          color: #1e40af;
          font-size: 20px;
        }

        .stats-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
        }

        .stats-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .stats-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }

        .stats-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .stats-header h3 {
          margin: 0;
          color: #1e40af;
          font-size: 18px;
        }

        .utilization-rate {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 700;
        }

        .utilization-rate.high {
          background: #fecaca;
          color: #dc2626;
        }

        .utilization-rate.medium {
          background: #fed7aa;
          color: #ea580c;
        }

        .utilization-rate.low {
          background: #d1fae5;
          color: #059669;
        }

        .stats-details {
          margin-bottom: 16px;
        }

        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .stat-label {
          color: #6b7280;
        }

        .stat-value {
          font-weight: 600;
          color: #374151;
        }

        .utilization-bar {
          width: 100%;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .utilization-fill {
          height: 100%;
          background: linear-gradient(90deg, #10b981, #059669);
          transition: width 0.3s ease;
        }

        /* モーダル */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(5px);
        }

        .reservation-modal {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          color: #1e40af;
          font-size: 20px;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #6b7280;
          padding: 4px;
          border-radius: 4px;
        }

        .modal-close:hover {
          background: #f3f4f6;
        }

        .modal-body {
          padding: 24px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          color: #374151;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid #e5e7eb;
        }

        .cancel-button {
          background: #6b7280;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .cancel-button:hover {
          background: #4b5563;
        }

        .submit-button {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .submit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ローディングとエラー */
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .loading-spinner {
          font-size: 18px;
          color: #1e40af;
          font-weight: 600;
        }

        .error-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .error-message {
          text-align: center;
          padding: 32px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          max-width: 400px;
        }

        .error-message h2 {
          color: #dc2626;
          margin-bottom: 16px;
        }

        .error-message p {
          color: #6b7280;
          margin-bottom: 24px;
        }

        .retry-button {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .retry-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        /* レスポンシブ */
        @media (max-width: 768px) {
          .manager-header {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .summary-cards {
            grid-template-columns: repeat(2, 1fr);
            padding: 16px;
          }

          .tab-content {
            margin: 0 16px 16px 16px;
          }

          .calendar-grid {
            overflow-x: auto;
          }

          .table-header,
          .table-row {
            grid-template-columns: 80px 100px 80px 1fr 80px 60px 60px;
            font-size: 12px;
          }


          .search-filters {
            grid-template-columns: 1fr;
          }

          .stats-cards {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }

        /* 予約詳細モーダル */
        .reservation-detail-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 480px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          position: relative;
        }

        .detail-modal-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .header-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .action-btn {
          background: none;
          border: none;
          padding: 8px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 16px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
        }

        .action-btn:hover {
          background: #f3f4f6;
        }

        .close-btn {
          color: #6b7280;
          font-size: 20px;
          font-weight: bold;
        }

        .detail-modal-body {
          padding: 20px;
        }

        .meeting-info {
          margin-bottom: 20px;
        }

        .meeting-status {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .status-dot {
          width: 12px;
          height: 12px;
          background: #3b82f6;
          border-radius: 50%;
        }

        .meeting-title {
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .meeting-datetime {
          margin-bottom: 4px;
          color: #6b7280;
          font-size: 14px;
        }

        .meeting-location {
          color: #6b7280;
          font-size: 14px;
        }

        .meeting-actions {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .join-btn {
          background: #4285f4;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          transition: background 0.2s ease;
        }

        .join-btn:hover {
          background: #3367d6;
        }

        .copy-btn {
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          padding: 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          transition: background 0.2s ease;
        }

        .copy-btn:hover {
          background: #e5e7eb;
        }

        .meeting-details {
          border-top: 1px solid #e5e7eb;
          padding-top: 20px;
        }

        .detail-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
        }

        .detail-icon {
          font-size: 16px;
          width: 20px;
          text-align: center;
          margin-top: 2px;
        }

        .detail-content {
          flex: 1;
        }

        .detail-title {
          font-size: 14px;
          color: #111827;
          margin-bottom: 4px;
        }

        .detail-value {
          font-size: 14px;
          color: #374151;
          margin-bottom: 2px;
          font-weight: 500;
        }

        .detail-description {
          white-space: pre-wrap;
          line-height: 1.5;
          max-height: 100px;
          overflow-y: auto;
        }

        .guest-list {
          margin: 16px 0;
        }

        .guest-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .guest-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
        }

        .guest-avatar.organizer {
          background: #10b981;
        }

        .guest-avatar.purple {
          background: #8b5cf6;
        }

        .guest-info {
          flex: 1;
        }

        .guest-name {
          display: block;
          font-size: 14px;
          color: #111827;
          margin-bottom: 2px;
        }

        .guest-role {
          font-size: 12px;
          color: #6b7280;
        }

        .meeting-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .notification-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .notification-icon {
          font-size: 14px;
        }

        .notification-text {
          font-size: 13px;
          color: #6b7280;
        }

        .organizer-info {
          font-size: 13px;
          color: #6b7280;
        }

        .detail-modal-footer {
          padding: 16px 20px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .footer-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .participation-text {
          font-size: 14px;
          color: #374151;
        }

        .response-buttons {
          display: flex;
          gap: 8px;
        }

        .response-btn {
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          color: #374151;
          transition: all 0.2s ease;
        }

        .response-btn:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .response-btn:first-child {
          background: #dbeafe;
          border-color: #93c5fd;
          color: #1e40af;
        }

        .response-btn:first-child:hover {
          background: #bfdbfe;
          border-color: #60a5fa;
        }
      `}</style>
    </div>
  )
}

export default MeetingRoomManager